import { User } from "oidc-client-ts";
import { IIamSettings } from "./iamSettings";
import AppView from "../../app.view";
import { IamConstants, IIamAppState, isMyAdmin, shouldLoginWithAppAuth, shouldLoginWithAS } from "../../services/iamService";
import { IamCredentialsActions } from "../../store/iamCredentials/actions";
import { EXTERNAL_AUTH_FAILED_MSG, ILoginUrlState } from "../../ui/login";
import { getCurrentHostName } from "../uri";
import IamApi from "./iamApi";
import { DataActions } from "../../store/data/actions";
import * as logger from "../logger";
import { handleApi } from "../error";
import { ICredentials, ICredentialsWithServer } from "../credentialsStore";
import { getTempIamManager } from "./iamManager";
import { getDatabaseNameFromUri } from "../databaseNameParsingUtilities";
import GeotabUser from "../../model/User";
import constants from "../../constants";
import NewIamApi from "./newIamApi";

/**
 * The signed-in user belongs to no database, or to several when none was chosen. The messages are the ones the login
 * has always reported; the type lets the QR sign-in tell the two apart from any other failure.
 */
export class IamDatabaseSelectionError extends Error {
    constructor(readonly reason: "none" | "several") {
        super(reason === "none" ? "Failed to get any database for user" : "Failed to find correct database for user");
        // Extending Error under an ES5 target breaks instanceof unless the prototype is restored.
        Object.setPrototypeOf(this, IamDatabaseSelectionError.prototype);
        this.name = "IamDatabaseSelectionError";
    }
}

/** The login was cancelled while it was still running. Nothing it had not already stored may be stored. */
export class IamLoginCancelledError extends Error {
    constructor() {
        super("The login was cancelled.");
        Object.setPrototypeOf(this, IamLoginCancelledError.prototype);
        this.name = "IamLoginCancelledError";
    }
}

export class IamFlow {

    constructor(
                private readonly app: AppView,
                private readonly iamSettings: IIamSettings,
                private readonly username: string,
                private readonly appState: IIamAppState,
                private readonly isAppAuthIamLoginPopupEnabled: boolean,
                private readonly addLoginAuditLog: Function,
                private readonly setCredentialsFromRemote: Function,
                private readonly updateState: Function,
                private readonly cancelLogin: Function,
                private readonly setLastDatabase: Function,
                private readonly setLastServer: Function,
                private readonly showMessage: Function
            ) {}

    iamLoginWithPopup(samlLoginUrl: string) {
        return new Promise((resolve, reject) => {
            let samlCallBack = (async (error, result) => {
                if (result) {
                    try {
                        let user;
                        await this.showMessage();
                        user = await this.deserializeResult(result);

                        this.loginSuccessCallBack(user, resolve, reject);
                    } catch (e) {
                        logger.error(logger.Source.IAM, `IAM popup login error during credential processing: ${ e instanceof Error ? e.message : String(e) }`, this.username);
                        reject(this.loginErrorCallBack(e));
                    }
                } else {
                    logger.error(logger.Source.IAM, `IAM popup login error: ${ error.message }`, this.username);
                    reject(this.loginErrorCallBack(error));
                }
            });

            let appAuthCallBack = async (error, result) => {
                if (result) {
                    await this.showMessage();

                    let profile = this.parseJwt(result.accessToken);
                    this.appAuthLoginSuccessCallBack(this.appState, isMyAdmin(profile), resolve, reject);
                } else {
                    logger.error(logger.Source.IAM, `AppAuth IAM popup login error: ${ error.message }`, this.username);
                    reject(this.loginErrorCallBack(error));
                }
            };

            if (this.isAppAuthIamLoginPopupEnabled && shouldLoginWithAppAuth()) {
                window.geotabModules?.auth.login({
                        clientId: this.iamSettings.clientId,
                        discoveryUri: constants.IAM.GET_DISCOVERY_URI(this.iamSettings.loginUrl),
                        username: this.username,
                        ephemeralSession: this.appState.addCoDriver
                }, appAuthCallBack);
            } else if (window.geotabModules?.sso) {
                if (shouldLoginWithAS()) {
                    window.geotabModules?.sso.samlLoginWithAS({
                                samlLoginUrl: samlLoginUrl,
                                ephemeralSession: true
                        }, samlCallBack);
                } else {
                    window.geotabModules.sso.samlLogin({samlLoginUrl: samlLoginUrl}, samlCallBack);
                }
            }
        });
    }

    async login(appState: IIamAppState) {
        this.app.context.iamService.init(this.iamSettings, this.username, this.iamLoginWithPopup.bind(this));
        return this.app.context.iamService.login(appState, this.username);
    }

    /**
     * Completes an IAM login from a user whose tokens were obtained outside the popup flow
     * (MYG-151497: the OAuth device authorization grant used by the Tesla Semi QR sign-in).
     * Runs the same success path as a popup login: token store, database lookup, user checks, audit log.
     * `isCancelled` is checked after every awaited step, so a login the driver cancelled stops there and does not
     * finish behind their back. The last check is the one after the user lookup; `onCommitted` is called right after
     * it, because from then on the login can no longer be cancelled (the hand-off that follows is not guarded).
     */
    loginWithUser(user: User, isCancelled: () => boolean = () => false, onCommitted: () => void = () => undefined): Promise<User | null> {
        this.app.context.iamService.init(this.iamSettings, this.username, this.iamLoginWithPopup.bind(this));
        return new Promise((resolve, reject) => {
            this.loginSuccessCallBack(user, resolve, reject, isCancelled, onCommitted).catch(reject);
        });
    }

    /**
     * Removes the user this attempt stored, but only while the entry is still this attempt's. The key is the same for
     * every attempt by the same driver on the same client, so a late cleanup must not delete a newer attempt's entry.
     */
    private removeStoredUserIfOwned(userStoreKey: string, user: User) {
        const stored = this.app.store.getState().iamCredentials[userStoreKey]?.user;
        let storedAccessToken: string | undefined;
        try {
            storedAccessToken = stored ? JSON.parse(stored).access_token : undefined;
        } catch {
            storedAccessToken = undefined;
        }
        storedAccessToken === user.access_token && this.app.store.dispatch(IamCredentialsActions.removeUser(userStoreKey));
    }

    private async getDatabase(appState: IIamAppState, getDatabase: () => Promise<string[]>) {
        let databases = appState && appState.database
            ? [appState.database]
            : await getDatabase();
        return databases;
    }

    private async loginSuccessCallBack(user: User | null, resolve, reject, isCancelled: () => boolean = () => false, onCommitted: () => void = () => undefined) {
        if (!user) {
            window.history.replaceState({}, document.title, `${ window.location.hash }`);
            reject(this.loginErrorCallBack(new Error("Failed to get user")));
            return;
        }

        let iamApi: IamApi = new IamApi(this.app.userService, this.app.store, "IamFlow", this.username);
        let appState = user.state as IIamAppState;
        let database = appState && appState.database;
        let server = (appState && appState.server) ? appState.server : getCurrentHostName();
        let iamJwt = user.access_token;

        let addCoDriver = appState && appState.addCoDriver;
        let databaseFromUri = getDatabaseNameFromUri(window.location) + "/";
        let url = addCoDriver ? databaseFromUri : "";
        window.history.replaceState({}, document.title, `/${ url }drive/${ addCoDriver ? IamConstants.UrlHashWithAddCoDriver : IamConstants.UrlHash }`);

        // The username is set the same as _userStoreKey in usermanager to match the key usermanager uses
        // If it ever changed in the oidc-client-ts we will need to change it as well
        // https://github.com/authts/oidc-client-ts/blob/main/src/UserManager.ts#L742
        const userStoreKey = this.username + `user:${ this.iamSettings.loginUrl }:${ this.iamSettings.clientId }`;
        const throwIfCancelled = () => {
            if (isCancelled()) {
                throw new IamLoginCancelledError();
            }
        };

        try {
            this.app.store.dispatch(IamCredentialsActions.setUser({
                userName: userStoreKey,
                iamSettings: this.iamSettings,
                user: JSON.stringify(user)
            }));

            let credentialsFromRemote = { userName: this.username, database: (appState && appState.database) ? appState.database : "", iamJwt };
            this.app.api.setUser(credentialsFromRemote, server, this.iamSettings);

            let databases = await this.getDatabase(appState, () => iamApi.getUserDatabaseNames(this.username, iamJwt));
            throwIfCancelled();
            if (databases.length === 1) {
                database = databases[0];
            } else if (databases.length === 0) {
                throw new IamDatabaseSelectionError("none");
            } else {
                throw new IamDatabaseSelectionError("several");
            }

            if (isMyAdmin(user.profile)) {
                await iamApi.callUpsertUser(this.username, iamJwt, database);
                throwIfCancelled();
            }

            // check that the user logging in exists in the database and are not service Account user
            let geotabUser: GeotabUser = await iamApi.checkUserExist(this.username, iamJwt, database);
            throwIfCancelled();
            if (geotabUser && geotabUser.userAuthenticationType === constants.IAM.USER_SERVICE_ACCOUNT_AUTHENTICATION_TYPE) {
                throw new Error("Service account users are not allowed to login");
            }

            // Nothing below waits for the network before the hand-off starts, so this is the last point to cancel at.
            onCommitted();

            let newState = { iam: true } as ILoginUrlState;
            // Include the full OIDC token set so the native layer can create a
            // restorable IAM session via sessionEstablished. expires_at from
            // oidc-client-ts is in Unix seconds; convert to ms for OidcTokens.
            newState.credentials = {
                userName: this.username,
                database,
                iamJwt,
                oidcRefreshToken: user.refresh_token ?? undefined,
                oidcIdToken: user.id_token ?? undefined,
                oidcExpiresAt: typeof user.expires_at === "number" ? user.expires_at * 1000 : undefined
            };
            newState.server = server;

            this.addLoginAuditLog(iamApi, database);
            this.setLastDatabase(newState.credentials);
            this.setLastServer(server);

            this.app.store.dispatch(DataActions.setLastUser(this.username));
            logger.log(logger.Source.IAM, "IAM popup successfully signed in", this.username);
            this.setCredentialsFromRemote(newState, this.iamSettings);
            return resolve(user);
        } catch (error) {
            // A call that failed after the driver cancelled is a cancellation too, whatever it failed with: the generic
            // branch below signs out the shared IAM manager and clears the shared API credentials, which by now may
            // belong to a newer login.
            if (error instanceof IamLoginCancelledError || isCancelled()) {
                // Undo only what this attempt stored. The caller revokes the tokens it holds.
                this.removeStoredUserIfOwned(userStoreKey, user);
                this.app.api.getCurrentCredentials()?.credentials?.iamJwt === iamJwt && this.app.api.forget();
                return reject(error instanceof IamLoginCancelledError ? error : new IamLoginCancelledError());
            }

            this.app.api.forget();
            this.app.context.iamService.handleSilentSignout(this.username);
            logger.error(logger.Source.IAM, `IAM popup login error: ${ error.message }`, this.username);
            this.updateState({ isInProgress: false, error: EXTERNAL_AUTH_FAILED_MSG, stageTwoState: undefined });
            return reject(error);
        }
    }

    private async appAuthLoginSuccessCallBack(appState: IIamAppState, upsertAdminUser, resolve, reject) {
        let iamApi = new NewIamApi(this.app.userService, this.app.store, this.username, "getTempContext");
        let database = appState && appState.database;
        let server = (appState && appState.server) ? appState.server : getCurrentHostName();

        let addCoDriver = appState && appState.addCoDriver;
        let databaseFromUri = getDatabaseNameFromUri(window.location) + "/";
        let url = addCoDriver ? databaseFromUri : "";
        window.history.replaceState({}, document.title, `/${ url }drive/${ addCoDriver ? IamConstants.UrlHashWithAddCoDriver : IamConstants.UrlHash }`);

        try {
            let databases = await this.getDatabase(appState, () => iamApi.getUserDatabaseNames(this.username));
            if (databases.length === 1) {
                database = databases[0];
            } else if (databases.length === 0) {
                throw new Error("Failed to get any database for user");
            } else {
                throw new Error("Failed to find correct database for user");
            }
            let credentialsFromRemote = { userName: this.username, database };
            this.app.api.setUser(credentialsFromRemote, server, this.iamSettings);

            if (upsertAdminUser) {
                await iamApi.callUpsertUser(this.username, database);
            }

            // check that the user logging in exists in the database and are not service Account user
            let geotabUser: GeotabUser = await iamApi.checkUserExist(this.username, database);
            if (geotabUser && geotabUser.userAuthenticationType === constants.IAM.USER_SERVICE_ACCOUNT_AUTHENTICATION_TYPE) {
                throw new Error("Service account users are not allowed to login");
            }

            let newState = { iam: true } as ILoginUrlState;
            newState.credentials = { userName: this.username, database };
            newState.server = server;

            this.addLoginAuditLog(iamApi, database);
            this.setLastDatabase(newState.credentials);
            this.setLastServer(server);

            this.app.store.dispatch(DataActions.setLastUser(this.username));
            logger.log(logger.Source.IAM, "AppAuth IAM popup successfully signed in", this.username);
            this.setCredentialsFromRemote(newState);
            return resolve("");
        } catch (error) {
            this.app.api.forget();
            logger.error(logger.Source.IAM, `AppAuth IAM popup login error: ${ error.message }`, this.username);
            this.updateState({ isInProgress: false, error: EXTERNAL_AUTH_FAILED_MSG, stageTwoState: undefined });
            return reject(error);
        }
    }

    private loginErrorCallBack(error) {
        this.updateState({ error: EXTERNAL_AUTH_FAILED_MSG });
        this.cancelLogin();
        handleApi(error, true);
        return error;
    }

    private deserializeResult(result: any) {
        let url = "";
        if (shouldLoginWithAS()) {
            // iOS clients return the response url saved in the sessionid of the login credentials
            let credentialsWithServer = JSON.parse(result) as ICredentialsWithServer | null;
            let credentials = credentialsWithServer?.credentials as ICredentials | null;
            let sessionId = credentials?.sessionId as string | "";
            if (sessionId) {
                url = decodeURIComponent(sessionId);
            } else {
                return null;
            }
        } else {
            // Android clients return the response url directly
            url =  result;
        }

        let userManager = getTempIamManager(this.iamSettings);
        if (!userManager) {
            return null;
        }

        return userManager.signinRedirectCallback(url);
    }

    private parseJwt (token) {
        let base64Url = token.split(".")[1];
        let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        let jsonPayload = decodeURIComponent(window.atob(base64).split("").map(function(c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(""));

        return JSON.parse(jsonPayload);
    }
}